import React, { useState } from "react";
import { Link } from "gatsby";
import cx from "classnames";
import {
  IoArrowForwardSharp,
  IoArrowBackSharp,
  IoPaperPlane,
} from "react-icons/io5";
import {
  motion,
  useScroll,
  useVelocity,
  useSpring,
  useTransform,
  AnimatePresence,
} from "framer-motion";

import { useIsDesktop } from "../hooks/mediaQuery";
import { useStateContext } from "components/context";

export function SimpleButton(props) {
  return (
    <button {...props}>
      <div className="label">
        <p className="text">{props.name}</p>
      </div>

      <div className="circlewrap">
        <div className="circle" />
        <div className="arrow">
          <IoArrowForwardSharp className="icon" />
        </div>
      </div>
    </button>
  );
}

export function LinkButton(props) {
  return (
    <Link {...props}>
      <div className="label">
        <p className="text">{props.name}</p>
      </div>

      <div className="circlewrap">
        <div className="circle" />
        <div className="arrow">
          <IoArrowForwardSharp className="icon" />
        </div>
      </div>
    </Link>
  );
}

export function ActionButton({ isPending, icon, name, ...buttonProps }) {
  return (
    <button {...buttonProps}>
      <div className="label">
        <p className="text">{isPending ? "Отправка" : name}</p>
      </div>

      <div className="circlewrap">
        <div className="circle" />
        <div className="arrow">{icon}</div>
      </div>
    </button>
  );
}

export function WideButton(props) {
  return (
    <button {...props}>
      <div className="label">
        <p className="text">{props.name}</p>
      </div>

      <div className="circlewrap">
        <div className="circle" />
        <div className="arrow">{props.icon}</div>
      </div>
    </button>
  );
}

export function WideLink(props) {
  return (
    <Link to={props.to} className={props.className}>
      <div className="label">
        <p className="text">{props.name}</p>
      </div>

      <div className="circlewrap">
        <div className="circle" />
        <div className="arrow">{props.icon}</div>
      </div>
    </Link>
  );
}

export function LinkConnection(props) {
  return (
    <a
      href={props.href}
      target="_blank"
      rel="noreferrer"
      className={props.className}
    >
      <div className="label">
        <p className="text">{props.name}</p>
      </div>

      <div className="circlewrap">
        <div className="circle" />
        <div className="arrow">{props.icon}</div>
      </div>
    </a>
  );
}

export function BackButton({ hidden, className, ...buttonProps }) {
  return (
    <button
      className={cx("round-simple back", hidden && "hidden", className)}
      {...buttonProps}
    >
      <IoArrowBackSharp className="icon" />
      <IoArrowBackSharp className="hiddenicon" />
    </button>
  );
}

export function NextButton({ hidden, className, ...buttonProps }) {
  return (
    <button
      className={cx("round-simple next", hidden && "hidden", className)}
      {...buttonProps}
    >
      <IoArrowForwardSharp className="icon" />
      <IoArrowForwardSharp className="hiddenicon" />
    </button>
  );
}

export function SocialButton() {
  const isDesktop = useIsDesktop();

  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const offset = isDesktop ? 400 : 200;
  const isScrollingDown = scrollVelocity.get() > 0;
  const isScrollingUp = scrollVelocity.get() < 0;

  const yRaw = useTransform(scrollVelocity, (v) => {
    const clamped = Math.max(Math.min(v / offset, 8), -12);
    return -clamped;
  });

  const scaleRaw = useTransform(scrollVelocity, (v) => {
    const clamped = Math.max(Math.min(Math.abs(v) / offset, 1), 0);
    return 1 - clamped * 0.05;
  });

  const topSpringConfig = isScrollingUp
    ? { stiffness: 180, damping: 24, mass: 1.2 }
    : { stiffness: 280, damping: 30, mass: 0.8 };
  const bottomSpringConfig = isScrollingDown
    ? { stiffness: 180, damping: 24, mass: 1.2 }
    : { stiffness: 280, damping: 30, mass: 0.8 };

  const topY = useSpring(yRaw, topSpringConfig);
  const bottomY = useSpring(yRaw, bottomSpringConfig);
  const topScaleSpring = useSpring(scaleRaw, topSpringConfig);
  const bottomScaleSpring = useSpring(scaleRaw, bottomSpringConfig);
  const topScale = isDesktop ? topScaleSpring : 1;
  const bottomScale = isDesktop ? bottomScaleSpring : 1;

  return (
    <div className="social-buttons">
      <motion.a
        className="button"
        href="https://max.ru/id772579084195_biz"
        target="_blank"
        rel="noreferrer"
        style={{ y: topY, scale: topScale }}
      >
        <div className="back" />
        <div className="label">
          <span className="wrap">MAX-канал</span>
        </div>
        <div className="round">
          <img
            src="https://maxicons.ru/icons/Max_logo_black.svg"
            alt="MAX"
            className="icon maxblack"
          />
          <img
            src="https://maxicons.ru/icons/MAX.svg"
            alt="MAX"
            className="icon max"
          />
        </div>
      </motion.a>
      <motion.a
        className="button"
        href="https://t.me/MRC_club"
        target="_blank"
        rel="noreferrer"
        style={{ y: bottomY, scale: bottomScale }}
      >
        <div className="back" />
        <div className="label">
          <span className="wrap">Telegram-канал</span>
        </div>
        <div className="round">
          <IoPaperPlane className="icon black" />
          <IoPaperPlane className="icon blue" />
        </div>
      </motion.a>
    </div>
  );
}

export function TelegramButtonShort() {
  return (
    <a
      className="button-short"
      href="https://t.me/MRC_club"
      target="_blank"
      rel="noreferrer"
    >
      <div className="round">
        <IoPaperPlane className="icon black" />
        <IoPaperPlane className="icon blue" />
      </div>
    </a>
  );
}

export function MaxButtonShort() {
  return (
    <a
      className="button-short"
      href="https://max.ru/id772579084195_biz"
      target="_blank"
      rel="noreferrer"
    >
      <div className="round">
        <img
          src="https://maxicons.ru/icons/Max_logo_black.svg"
          alt="MAX"
          className="icon maxblack"
        />
        <img
          src="https://maxicons.ru/icons/MAX.svg"
          alt="MAX"
          className="icon max"
        />
      </div>
    </a>
  );
}

export function RequestSpeakerButton() {
  const { hideRequestSpeakerButton } = useStateContext();
  const isDesktop = useIsDesktop();
  const [choise, setChoise] = useState(false);

  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);

  const offset = isDesktop ? 400 : 200;

  const yRaw = useTransform(scrollVelocity, (v) => {
    const clamped = Math.max(Math.min(v / offset, 8), -12);
    return -clamped;
  });

  const y = useSpring(yRaw);

  const scaleRaw = useTransform(scrollVelocity, (v) => {
    const clamped = Math.max(Math.min(Math.abs(v) / offset, 1), 0);
    return 1 - clamped * 0.05;
  });

  const scaleSpring = useSpring(scaleRaw);

  const scale = isDesktop ? scaleSpring : 1;

  return (
    <motion.div className="request-speaker-button" style={{ y, scale }}>
      <div className={cx("wrap", hideRequestSpeakerButton && "hidden")}>
        <AnimatePresence mode="popLayout">
          {choise ? (
            <div key="select" className="select">
              <motion.button
                animate={{
                  scale: 1,
                  opacity: 1,
                  x: 0,
                  transition: {
                    duration: 1,
                    ease: [0.76, 0, 0.24, 1],
                    delay: 0.275,
                  },
                }}
                whileHover={{
                  scale: 0.95,
                  transition: {
                    duration: 0.4,
                    ease: [0.76, 0, 0.24, 1],
                    delay: 0,
                  },
                }}
                initial={{ scale: 0.95, x: -5, opacity: 0 }}
                exit={{ scale: 1.075, x: 0, opacity: 0 }}
                className="button back"
                onClick={() => setChoise(false)}
                key="back"
              >
                <IoArrowBackSharp className="arrow" />
              </motion.button>
              <motion.a
                key="telegram"
                animate={{
                  scale: 1,
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.8,
                    ease: [0.76, 0, 0.24, 1],
                    delay: 0.25,
                  },
                }}
                initial={{ scale: 1, y: 8, opacity: 0 }}
                exit={{ scale: 0.9, opacity: 0 }}
                href="https://t.me/MRC_SMI_bot"
                target="_blank"
                rel="noreferrer"
                className="button telegram"
              >
                <div className="label">
                  <p className="text">
                    <span>Telegram</span>
                  </p>
                </div>

                <div className="circlewrap">
                  <div className="circle" />
                  <div className="icon">
                    <IoPaperPlane className="arrow" />
                  </div>
                </div>
              </motion.a>
              <motion.a
                key="max"
                animate={{
                  scale: 1,
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    ease: [0.76, 0, 0.24, 1],
                    delay: 0.175,
                  },
                }}
                initial={{ scale: 1, y: 6, opacity: 0 }}
                exit={{ scale: 0.9, opacity: 0 }}
                href="https://max.ru/id772579084195_bot"
                target="_blank"
                rel="noreferrer"
                className="button max"
              >
                <div className="label">
                  <p className="text">
                    <span>MAX</span>
                  </p>
                </div>

                <div className="circlewrap">
                  <div className="circle" />
                  <div className="icon">
                    <svg viewBox="0 0 720 720" alt="MAX" className="arrow max">
                      <path
                        fill="currentColor"
                        d="M350.4 9.6C141.8 20.5 4.1 184.1 12.8 390.4c3.8 90.3 40.1 168 48.7 253.7 2.2 22.2-4.2 49.6 21.4 59.3 31.5 11.9 79.8-8.1 106.2-26.4 9-6.1 17.6-13.2 24.2-22 27.3 18.1 53.2 35.6 85.7 43.4 143.1 34.3 299.9-44.2 369.6-170.3C799.6 291.2 622.5-4.6 350.4 9.6zm-81 494.4c-11.3 8.8-22.2 20.8-34.7 27.7-18.1 9.7-23.7-.4-30.5-16.4-21.4-50.9-24-137.6-11.5-190.9 16.8-72.5 72.9-136.3 150-143.1 78-6.9 150.4 32.7 183.1 104.2 72.4 159.1-112.9 316.2-256.4 218.6z"
                      />
                    </svg>
                  </div>
                </div>
              </motion.a>
            </div>
          ) : (
            <motion.button
              key="toggle"
              animate={{
                scale: 1,
                y: 0,
                opacity: 1,
                transition: {
                  duration: 0.4,
                  ease: [0.76, 0, 0.24, 1],
                  delay: 0.15,
                },
              }}
              initial={{ scale: 0.95, opacity: 0 }}
              exit={{ scale: 1.025, opacity: 0 }}
              className="button toggle"
              onClick={() => setChoise(true)}
            >
              <div className="label">
                <p className="text">
                  Отправить запрос через <span>max</span> /{" "}
                  <span>telegram</span>
                </p>
              </div>

              <div className="circlewrap">
                <div className="circle" />
                <div className="icon">
                  <IoArrowForwardSharp className="arrow" />
                </div>
              </div>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
